import type { MetaChipTone } from "../../components/UIToolkit/Badges/MetaChip";
import { FONT_SIZE, INK } from "../../theme/tokens";
import type { RunStepStatus, ShiftOutcome } from "./shiftLineageGroups";

/**
 * Shared display vocabulary for the "Shifts & Punches" log. Lives here — not in a component — because
 * three surfaces render the same states and MUST label them identically: the grid row, its expanded
 * timeline, and the single-shift focus panel a support user lands on from a shift's raw punches.
 */

/** Where a shift stands now — the row's headline. Only "removed" is red: a re-sync that rewrote a shift
 * is routine bookkeeping, so it must not read as alarming as one that took a shift away. */
export const OUTCOME_TONE: Record<ShiftOutcome, MetaChipTone> = {
	active: "success",
	removed: "error",
	notCreated: "warning",
};

export const OUTCOME_LABEL_KEY: Record<ShiftOutcome, string> = {
	active: "employeeChangelog.shiftLineage.outcome.active",
	removed: "employeeChangelog.shiftLineage.outcome.removed",
	notCreated: "employeeChangelog.shiftLineage.outcome.notCreated",
};

export const OUTCOME_HINT_KEY: Record<ShiftOutcome, string> = {
	active: "employeeChangelog.shiftLineage.outcome.activeHint",
	removed: "employeeChangelog.shiftLineage.outcome.removedHint",
	notCreated: "employeeChangelog.shiftLineage.outcome.notCreatedHint",
};

/** Reuses the existing per-action product copy so the vocabulary can't drift between the two views. */
export const STEP_LABEL_KEY: Record<RunStepStatus, string> = {
	created: "employeeChangelog.shiftLineage.action.insert",
	replaced: "employeeChangelog.shiftLineage.change.replaced",
	removed: "employeeChangelog.shiftLineage.action.delete",
	blocked: "employeeChangelog.shiftLineage.action.skip",
	ignored: "employeeChangelog.shiftLineage.action.ignore",
	deferred: "employeeChangelog.shiftLineage.action.defer",
};

export const STEP_TONE: Record<RunStepStatus, MetaChipTone> = {
	created: "success",
	replaced: "warning",
	removed: "error",
	blocked: "warning",
	ignored: "neutral",
	deferred: "info",
};

/** The small uppercase section label used throughout the log's detail areas. */
export const SECTION_LABEL_SX = {
	fontSize: FONT_SIZE.LABEL_XS,
	fontWeight: 700,
	letterSpacing: "0.06em",
	textTransform: "uppercase",
	color: INK.MUTED,
} as const;
